import React from 'react';
import classnames from 'classnames';
import { PapiUser } from '@wix/da-papi-types';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import Plus from '@wix/da-ds/pkg/Icons/16x16/Add';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import Check from '@wix/da-ds/pkg/Icons/16x16/Check';
import Close from '@wix/da-ds/pkg/Icons/16x16/Close';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import SisuActionPopover from '../../ActionBar/_partials/SisuActionPopover/SisuActionPopover';
import { useRestrictToLoggedIn } from '../../utils/hooks/useRestrictToLoggedIn';
import { useWatchButton } from '../../WatchButton/useWatchButton';

import s from './AuthorWatchButton.scss';

export interface Props {
  author: PapiUser;
  className?: string;
  watchingClassname?: string;
  iconClassName?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  isWatchingBack?: boolean;
  ghostButton?: boolean;
  isSummary?: boolean;
  onWatch?: () => void;
  onToggleWatchSuccess?: (isWatchingBack?: boolean) => void;
  /**
   * When set, invoked instead of the default watch toggle handler (e.g. SISU popover no-op).
   */
  onClickOverride?: (event: React.MouseEvent) => void;
}

export const AuthorWatchButton: React.FC<Props> = ({
  author,
  className,
  children,
  onWatch,
  iconClassName,
  isWatchingBack,
  ghostButton,
  watchingClassname,
  isSummary,
  disabled,
  onToggleWatchSuccess,
  onClickOverride,
}) => {
  const isSisuPopoverEnabled = useFlag('sisu_popover');
  const { isLoggedIn } = useRestrictToLoggedIn();
  const { onClick, text, isWatching, biData } = useWatchButton(
    author,
    author?.isWatching,
    isWatchingBack,
    disabled,
    onToggleWatchSuccess
  );

  const buttonContent = (
    <>
      <Plus
        disableDefaultColors
        className={classnames(s['plus-icon'], iconClassName)}
        size={IconSize.SMALLER}
      />
      <Close
        disableDefaultColors
        className={classnames(s['close-icon'], iconClassName)}
        size={IconSize.SMALLER}
      />
      {isWatching && (
        <>
          <Check
            disableDefaultColors
            className={classnames(s['check-icon'], iconClassName)}
            size={IconSize.SMALLER}
          />
        </>
      )}
      {children || text}
    </>
  );
  const onClickHandler = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
    onWatch?.();
  };

  const isSisuAnonWatch = isSisuPopoverEnabled && !isLoggedIn && !isWatching && !disabled;

  const effectiveClick =
    onClickOverride !== undefined ? onClickOverride : isSisuAnonWatch ? () => {} : onClickHandler;

  /*
   * BiButton logs watch intent on mousedown (see useBiClickLogger), not on click.
   * When we noop click for SISU or replace it via onClickOverride, omit biData so we do not emit a false watch.
   */
  const effectiveBiData = isSisuAnonWatch || onClickOverride !== undefined ? undefined : biData;

  if (ghostButton) {
    const inner = (
      <GhostButton
        size={isSummary ? 'medium' : 'small'}
        variant={'main'}
        biData={effectiveBiData}
        className={classnames(
          s['root-ghost'],
          isWatching && s['watching'],
          isWatching && watchingClassname,
          className
        )}
        onClick={effectiveClick}
        disabled={disabled || (isSummary && isWatching)}
      >
        {buttonContent}
      </GhostButton>
    );
    return isSisuAnonWatch ? (
      <SisuActionPopover restrictedActionName={ReferralType.WATCH}>{inner}</SisuActionPopover>
    ) : (
      inner
    );
  }

  const inner = (
    <TextButton
      variant="approval-green"
      size="small"
      biData={effectiveBiData}
      className={classnames(
        s['root'],
        isWatching && s['watching'],
        isWatching && watchingClassname,
        className
      )}
      onClick={effectiveClick}
      disabled={disabled}
    >
      {buttonContent}
    </TextButton>
  );
  return isSisuAnonWatch ? (
    <SisuActionPopover restrictedActionName={ReferralType.WATCH}>{inner}</SisuActionPopover>
  ) : (
    inner
  );
};
AuthorWatchButton.displayName = 'AuthorWatchButton';

export default AuthorWatchButton;
